import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { TOS_ROLE_SCOPES, type TosRoleScope } from "./compliance-types";
import { orderRoleScopes } from "./role-scopes";

export const ROLE_SCOPES_QUERY_KEY = ["compliance", "role-scopes"] as const;

/**
 * Scopes the BO starts from before the API answers — the compile-time
 * constant, used ONLY while loading or on error. Once the query
 * resolves, the catalog list wins. Kept ordered so the fallback
 * renders identically to a successful fetch of the same scopes.
 */
const FALLBACK_SCOPES: readonly TosRoleScope[] = orderRoleScopes(TOS_ROLE_SCOPES);

interface CatalogResponse {
  readonly roleScopes?: unknown;
}

/**
 * `GET /api/admin/tos/catalog` (API-TOS-12) answers
 * `{ roleScopes: ["GUEST", ..., "STAFF"] }` — every scope a document or
 * requirement can target. A response without the array is a contract
 * break, not an empty catalog, so it fails the query and the fallback
 * list keeps the screens usable.
 */
async function fetchRoleScopes(): Promise<readonly TosRoleScope[]> {
  const catalog = await httpClient<CatalogResponse>("/api/admin/tos/catalog");
  if (!Array.isArray(catalog?.roleScopes)) {
    throw new Error("ToS catalog response is missing roleScopes[]");
  }
  return orderRoleScopes(catalog.roleScopes);
}

export interface TosRoleScopesResult {
  /** Ordered scopes: known first, then any others alphabetically. */
  readonly scopes: readonly TosRoleScope[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  /** True while `scopes` is the hard-coded fallback (loading / error). */
  readonly isFallback: boolean;
}

export function useTosRoleScopes(): TosRoleScopesResult {
  const query = useQuery({
    queryKey: ROLE_SCOPES_QUERY_KEY,
    queryFn: fetchRoleScopes,
    staleTime: 60_000,
  });

  return useMemo(
    () => ({
      scopes: query.data ?? FALLBACK_SCOPES,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      isFallback: query.data == null,
    }),
    [query.data, query.isLoading, query.isError, query.error],
  );
}
